import { format } from "date-fns";

/**
 * Record provenance (#981): who or what created / last changed a row when it
 * wasn't a signed-in staff member. The values are stamped by the
 * stamp_record_provenance DB trigger into `created_via` / `updated_via`.
 *
 * Keep this list in step with public.is_known_write_via() in the
 * add_record_provenance_columns migration — the DB CHECK rejects anything else.
 */
export const WRITE_VIA = [
  "staff_ui",
  "plan_wizard",
  "landing_page",
  "menu_inquiry",
  "online_order",
  "customer",
  "square_sync",
  "ghl_import",
  "ghl_sync",
  "automation",
  "legacy_import",
  "untraced",
] as const;

export type WriteVia = (typeof WRITE_VIA)[number];

export function isWriteVia(value: unknown): value is WriteVia {
  return typeof value === "string" && (WRITE_VIA as readonly string[]).includes(value);
}

/** The channel on its own, used after "System · " or after a staff name. */
const VIA_LABELS: Record<Exclude<WriteVia, "staff_ui" | "untraced">, string> = {
  plan_wizard: "Plan wizard (customer)",
  landing_page: "Landing page form (customer)",
  menu_inquiry: "Menu inquiry form (customer)",
  online_order: "Online order (customer)",
  customer: "Public site (customer)",
  square_sync: "Square sync",
  ghl_import: "Imported from GHL",
  ghl_sync: "GHL sync",
  automation: "Automation",
  legacy_import: "Imported from legacy data",
};

const IMPORT_SOURCES: Partial<Record<WriteVia, { created: string; updated: string }>> = {
  ghl_import: { created: "GHL", updated: "the GHL import" },
  legacy_import: { created: "legacy data", updated: "the legacy import" },
};

const UNTRACED_LABEL = "System · untraced";
const REMOVED_STAFF_LABEL = "Staff member (account removed)";

export interface ProvenanceInput {
  /** Staff display name from the created_by / updated_by profile, if any. */
  name?: string | null;
  /** The row's created_via / updated_via value. */
  via?: string | null;
  /** The matching timestamp; only used for the import month. */
  at?: string | null;
  action?: "created" | "updated";
}

export interface Provenance {
  /** The label on its own, e.g. "System · Square sync" or "Frank". */
  label: string;
  /** Sentence lead, e.g. "Created by" or "Imported from". */
  lead: string;
  /** Sentence subject rendered in bold after the lead. */
  subject: string;
  /** True when nothing explains the row; the UI logs it so it gets fixed. */
  untraced: boolean;
}

function importMonth(at?: string | null): string {
  if (!at) return "";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return ` (${format(d, "MMM yyyy")})`;
}

export function describeProvenance({ name, via, at, action = "created" }: ProvenanceInput): Provenance {
  const defaultLead = action === "created" ? "Created by" : "Last edited by";
  const make = (label: string, untraced = false): Provenance => ({ label, lead: defaultLead, subject: label, untraced });
  const known = isWriteVia(via) ? via : null;
  const displayName = name?.trim() || null;

  if (displayName) {
    // A known person did it. Keep the channel when it wasn't the admin UI,
    // e.g. a staff member submitting /plan from a signed-in browser.
    if (!known || known === "staff_ui" || known === "untraced") return make(displayName);
    return make(`${displayName} · ${VIA_LABELS[known]}`);
  }

  if (!known || known === "untraced") return make(UNTRACED_LABEL, true);
  if (known === "staff_ui") return make(REMOVED_STAFF_LABEL);
  if (known === "customer") return make("Customer · public site");

  const imported = IMPORT_SOURCES[known];
  if (imported) {
    const label = `${VIA_LABELS[known]}${importMonth(at)}`;
    return action === "created"
      ? { label, lead: "Imported from", subject: imported.created, untraced: false }
      : { label, lead: "Last updated by", subject: imported.updated, untraced: false };
  }

  return make(`System · ${VIA_LABELS[known]}`);
}
